// Landing-page entry — handles the waitlist form submit.
//
// Posts to POST /waitlist on the backend. The form collects six required
// fields (name / email / phone / company / location / trade). Response either
// confirms the entry was recorded (new or duplicate) or surfaces a validation
// error. Keeps UX trust-first: never tells the user their email is "already
// registered" (that leaks existence), just thanks them.

import { TRADES } from "./lib/trades";

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "https://tradesview-7hynw6lk4q-uc.a.run.app";

const form = document.getElementById("waitlist-form") as HTMLFormElement | null;
const nameInput = document.getElementById("wl-name") as HTMLInputElement | null;
const emailInput = document.getElementById("wl-email") as HTMLInputElement | null;
const phoneInput = document.getElementById("wl-phone") as HTMLInputElement | null;
const companyInput = document.getElementById("wl-company") as HTMLInputElement | null;
const locationInput = document.getElementById("wl-location") as HTMLInputElement | null;
const tradeSelect = document.getElementById("wl-trade") as HTMLSelectElement | null;
const submitBtn = document.getElementById("wl-submit") as HTMLButtonElement | null;
const statusEl = document.getElementById("wl-status") as HTMLDivElement | null;

// Populate the trade dropdown from the canonical taxonomy (single source of
// truth shared with the backend enum + iOS) so the wire value the form posts
// is always one the backend accepts.
if (tradeSelect) {
  for (const { value, label } of TRADES) {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = label;
    tradeSelect.appendChild(opt);
  }
}

/** A canonical RFC-4122 v4 UUID, from the strongest source available.
 *
 * `crypto.randomUUID()` is unavailable on http origins in some browsers and on
 * older Safari, so the two fallbacks keep the same SHAPE — the server accepts
 * exactly one format and rejects everything else rather than reshaping it. */
function newUuidV4(): string {
  const c = typeof crypto !== "undefined" ? crypto : undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === "function") {
    c.getRandomValues(b);
  } else {
    for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  }
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant 10x
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function setStatus(kind: "ok" | "err" | "idle", text: string): void {
  if (!statusEl) return;
  statusEl.className = kind === "idle" ? "status" : `status ${kind}`;
  statusEl.textContent = text;
}

if (form && nameInput && emailInput && phoneInput && companyInput && locationInput && tradeSelect && submitBtn) {
  // Idempotency identity for the in-flight submit — see mint site below.
  let currentSubmissionId: string | null = null;

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();

    const name = nameInput.value.trim();
    const email = emailInput.value.trim().toLowerCase();
    const phone = phoneInput.value.trim();
    const company = companyInput.value.trim();
    const location = locationInput.value.trim();
    const tradeType = tradeSelect.value;

    // Minimal client-side checks — backend is the authoritative validator.
    // Walk the fields in form order so the first empty one gets focus.
    if (!name) {
      setStatus("err", "Please enter your name.");
      nameInput.focus();
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("err", "Please enter a valid email address.");
      emailInput.focus();
      return;
    }
    if (!phone) {
      setStatus("err", "Please enter your phone number.");
      phoneInput.focus();
      return;
    }
    if (!company) {
      setStatus("err", "Please enter your company.");
      companyInput.focus();
      return;
    }
    if (!location) {
      setStatus("err", "Please enter your location.");
      locationInput.focus();
      return;
    }
    if (!tradeType) {
      setStatus("err", "Please select your trade.");
      tradeSelect.focus();
      return;
    }

    submitBtn.disabled = true;
    setStatus("idle", "Sending…");

    // One id per SUBMIT: minted on the first attempt, reused verbatim on a
    // retry of that same submit (network blip, 5xx), cleared only on success.
    // The server keys the immutable consent receipt by it, so a retry
    // converges on one occurrence while a later fresh submit appends a new
    // one (Codex round-6: submission identity, not content identity).
    // The server validates this byte-for-byte against ONE canonical format (a
    // UUID) and rejects anything else rather than reshaping it, so every path
    // here — including the pre-randomUUID fallback — must emit a real UUID. A
    // fallback of any other shape would 400 an older browser's signup away.
    if (!currentSubmissionId) {
      currentSubmissionId = newUuidV4();
    }

    // Capture the tick ONCE, before any await. The checkbox stays enabled
    // during the request (only the button is disabled), so re-reading
    // `.checked` after the fetch reports whatever the visitor did in the
    // meantime: unticking mid-flight would send sms_consent:true and then
    // evaluate as unticked, silently swallowing a real capture failure — the
    // one outcome the notice below exists to prevent.
    const ticked =
      (document.getElementById("wl-sms-consent") as HTMLInputElement | null)
        ?.checked === true;

    try {
      const resp = await fetch(`${API_BASE}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          company,
          location,
          trade_type: tradeType,
          // Read from .checked, not FormData — an unchecked box is absent from
          // FormData and a checked one serialises as "on". Always sent, so an
          // explicit false is recorded rather than inferred from a missing key.
          sms_consent: ticked,
          sms_consent_text_version: "2026-08-15.wl.v2",
          submission_id: currentSubmissionId,
          // Referrer + UA help us see where early interest is coming from
          // without running a heavier analytics stack.
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
        }),
      });

      if (!resp.ok) {
        // 4xx from the backend — show its message if it gave us one
        let msg = "Something went wrong. Try again?";
        try {
          const body = await resp.json();
          if (typeof body?.detail === "string") msg = body.detail;
        } catch {
          // ignore body-parse failure
        }
        setStatus("err", msg);
        submitBtn.disabled = false;
        return;
      }

      // Success — both "created" and "already exists" land here; we
      // present them identically to avoid leaking existence. The one honest
      // exception: if the visitor TICKED the consent box and the server
      // reports it could not record the consent, say so — silence would leave
      // them believing they opted in when no record exists (never-quiet-fail).
      // sms_consent_recorded is identical on both paths, so no existence leak.
      // Fail-closed on the consent claim (Codex round-2): if the box was
      // ticked, only an explicit `sms_consent_recorded === true` counts as
      // recorded. A missing field or unparseable body — version-skew during a
      // rollout, a proxy mangling the response — must NOT read as success,
      // because telling someone they opted in when no record exists is the
      // exact lie this field exists to prevent.
      let consentNote = "";
      if (ticked) {
        let recorded = false;
        try {
          const body = await resp.json();
          recorded = body?.sms_consent_recorded === true;
        } catch {
          recorded = false;
        }
        if (!recorded) {
          consentNote =
            " One thing: we couldn't save your text-message preference —" +
            " you'll hear from us by email.";
        }
      }
      setStatus("ok", "Thanks — you're on the list. We'll be in touch." + consentNote);
      currentSubmissionId = null; // this submit is done; the next one is new
      form.reset();
      submitBtn.disabled = true;
      submitBtn.textContent = "Submitted";
    } catch (err) {
      setStatus("err", "Couldn't reach the server. Try again?");
      submitBtn.disabled = false;
      // eslint-disable-next-line no-console
      console.error("waitlist submit failed", err);
    }
  });
}
